import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, Settings, LogOut } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui';
import { useAuth, type AuthUser } from '@/features/auth/auth-store';
import { useLogout } from '@/features/auth/use-logout';
import { useOrg } from '@/features/org/use-org';

const MENU_ITEMS = [
  { icon: User, label: 'Профиль', hint: 'Личные данные и подписка', to: '/profile' },
  { icon: Settings, label: 'Настройки', hint: 'Доставка, курсы, PDF', to: '/org/settings' },
] as const;

function getInitials(user: AuthUser): string {
  if (user.full_name) {
    return user.full_name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('');
  }
  return user.email.charAt(0).toUpperCase();
}

function MenuIconChip({ icon: Icon }: { icon: typeof User }) {
  return (
    <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-surface-high text-primary">
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

export function UserMenu() {
  const { user, status } = useAuth();
  const { data: org, isLoading: orgLoading, isError: orgError } = useOrg();
  const navigate = useNavigate();
  const logout = useLogout();

  if (!user) return null;

  const orgLine = orgLoading ? '' : orgError ? 'Ошибка загрузки' : org?.name;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Меню пользователя"
          disabled={status === 'loading'}
          className="flex h-9 items-center gap-2 rounded-md pl-1 pr-2 transition-colors hover:bg-surface-high focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
        >
          <Avatar className="h-[30px] w-[30px] shrink-0">
            <AvatarImage src={user.avatar_url} alt={user.full_name ?? user.email} />
            <AvatarFallback className="text-xs bg-gradient-to-br from-[#8ed5ff] to-[#38bdf8] text-primary-foreground font-semibold">
              {getInitials(user)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden min-w-0 flex-col items-start leading-tight sm:flex">
            <span className="max-w-[140px] truncate text-xs font-semibold text-foreground">
              {user.full_name ?? user.email}
            </span>
            <span className="max-w-[140px] truncate text-[11px] text-outline-faint">
              {orgLine}
            </span>
          </span>
          <ChevronDown className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground sm:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[288px] p-0 overflow-hidden">
        <div className="flex items-center gap-2.5 px-3.5 py-3">
          <Avatar className="h-[38px] w-[38px] shrink-0">
            <AvatarImage src={user.avatar_url} alt={user.full_name ?? user.email} />
            <AvatarFallback className="text-sm bg-gradient-to-br from-[#8ed5ff] to-[#38bdf8] text-primary-foreground font-semibold">
              {getInitials(user)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-foreground">
              {user.full_name ?? user.email}
            </p>
            {org?.name && (
              <p className="truncate text-[11.5px] text-outline-faint">{org.name}</p>
            )}
          </div>
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="p-1.5">
          {MENU_ITEMS.map(({ icon, label, hint, to }) => (
            <DropdownMenuItem
              key={to}
              onSelect={() => navigate(to)}
              className="gap-2.5 rounded-md px-2 py-2"
            >
              <MenuIconChip icon={icon} />
              <span className="flex flex-col">
                <span className="text-[12.5px] font-medium leading-tight">{label}</span>
                <span className="text-[11px] leading-tight text-muted-foreground">{hint}</span>
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onSelect={() => void logout()}
            className="gap-2.5 rounded-md px-2 py-2"
          >
            <MenuIconChip icon={LogOut} />
            <span className="flex flex-col">
              <span className="text-[12.5px] font-medium leading-tight">Выйти</span>
              <span className="text-[11px] leading-tight text-muted-foreground">Завершить сеанс</span>
            </span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
